import { Tooltip } from '@chakra-ui/react'
import ApplyV2Button from '@core/components/ApplyDialogV2/ApplyV2Button'
import ApplyV2DataRow from '@core/components/ApplyDialogV2/ApplyV2DataRow'
import type { ApplyV2TempCourseFile } from '@core/app/types/applyV2Type'
import FileUploaderV2List from '@core/components/ApplyDialogV2/FileUploaderV2List'
import formatPhoneNumber from '@core/utils/helpers/formatPhoneNumber'
import useApplyV2NavTranslations from '@core/utils/hooks/useApplyV2NavTranslations'
import useFormattedDatesPeriod from '@core/utils/hooks/useFormattedDatesPeriod'
import React, { useEffect, useRef, useState } from 'react'
import clsx from 'clsx'
import './ApplyV2Card.scss'

interface ApplyV2CardProps {
    id?: string | number
    className?: string
    title?: React.ReactNode
    headerDescription?: string
    isLoading?: boolean
    isButtonsVisible?: boolean
    onEdit?: () => void
    onDelete?: () => void
    dateSection?: React.ReactNode
    phoneSection?: React.ReactNode
    emailSection?: React.ReactNode
    localeSection?: React.ReactNode
    children?: React.ReactNode
    files?: ApplyV2TempCourseFile[] | null
    phone?: {
        prefix?: string | null
        number?: string | null
    }
    date?: {
        startDate?: string | null
        endDate?: string | null
        startYear?: string | null
        endYear?: string | null
        startMonth?: string | null
        endMonth?: string | null
        startDay?: string | null
        endDay?: string | null
        dateFormat?: 'D MMM YYYY' | 'MMM YYYY' | 'MMMM YYYY'
        showMonths?: boolean
        isShowPresent?: false
    }
}

const baseClass = 'ApplyV2Card'

function ApplyV2Card({
    id,
    className,
    title,
    headerDescription,
    isLoading = false,
    isButtonsVisible = true,
    onEdit = () => {},
    onDelete = () => {},
    dateSection,
    children,
    files = [],
    phoneSection,
    emailSection,
    localeSection,
    date = {},
    phone = {},
}: ApplyV2CardProps) {
    const { getTitle } = useApplyV2NavTranslations()
    const [isExpanded, setIsExpanded] = useState(false)
    const [needsExpand, setNeedsExpand] = useState(false)
    const textRef = useRef<HTMLDivElement>(null)
    const { formatDateRange, formatDuration } = useFormattedDatesPeriod()

    useEffect(() => {
        if (textRef.current && children) {
            const lineHeight = 18
            const maxHeight = lineHeight * 3
            setNeedsExpand(textRef.current.scrollHeight > maxHeight)
        }
    }, [children])

    const periodDate = formatDateRange(date.startDate, date.endDate, {
        startYear: date?.startYear,
        endYear: date?.endYear,
        startMonth: date?.startMonth,
        endMonth: date?.endMonth,
        startDay: date?.startDay,
        endDay: date?.endDay,
        dateFormat: date?.dateFormat,
        fromDate: date.startDate,
        toDate: date.endDate,
        isShowPresent: date.isShowPresent,
    })

    const duration =
        date.startDate || date.endDate
            ? formatDuration(date.startDate, date.endDate)
            : formatDuration(null, null, {
                  startYear: date?.startYear,
                  endYear: date?.endYear,
                  startMonth: date?.startMonth,
                  endMonth: date?.endMonth,
                  startDay: date?.startDay,
                  endDay: date?.endDay,
              })

    // const formatPhoneNumber = (phone?: { prefix?: string | null; number?: string | null } | null): string => {
    //     if (!phone?.number) return ''
    //     const formattedNumber = phone.number.replace(/(\d{3})(?=\d)/g, '$1 ').trim()
    //     const cleanPrefix = phone.prefix?.replace(/^\+/, '')
    //     return cleanPrefix ? `+${cleanPrefix} ${formattedNumber}` : formattedNumber
    // }

    const phoneValue = formatPhoneNumber(phone)

    return (
        <div
            className={clsx(baseClass, className, {
                [`${baseClass}--loading`]: isLoading,
                // [`${baseClass}--buttons-visible`]: isButtonsVisible,
                [`${className}--loading`]: isLoading && className,
            })}
        >
            <div
                className={clsx(`${baseClass}__header--wrapper`, {
                    [`${className}__header--wrapper`]: className,
                })}
            >
                <div
                    className={clsx(`${baseClass}__header`, {
                        [`${className}__header`]: className,
                    })}
                >
                    <div
                        className={clsx(`${baseClass}__header--title`, {
                            [`${className}__header--title`]: className,
                        })}
                    >
                        {title}
                    </div>
                    {isButtonsVisible && (
                        <div
                            className={clsx(`${baseClass}__header--buttons`, {
                                [`${className}__header--buttons`]: className,
                            })}
                        >
                            <Tooltip
                                hasArrow
                                isDisabled={isLoading}
                                openDelay={500}
                                label={getTitle('106', 'Edit')}
                                // placement={'right'}
                                padding={'8px 16px'}
                                borderRadius={'4px'}
                                backgroundColor={'#19191A'}
                                fontWeight={600}
                                fontSize={'12px'}
                                shouldWrapChildren
                            >
                                <ApplyV2Button
                                    className={clsx(`${baseClass}__header--buttons-btn`, {
                                        [`${className}__header--buttons-btn`]: className,
                                    })}
                                    variant={'transparent'}
                                    size={'small'}
                                    onClick={() => {
                                        if (onEdit) {
                                            onEdit()
                                        }
                                    }}
                                    leftIcon={
                                        <svg
                                            width="16"
                                            height="16"
                                            viewBox="0 0 16 16"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <path
                                                fillRule="evenodd"
                                                clipRule="evenodd"
                                                d="M10.282 2.56998C10.2327 2.52067 10.1527 2.52067 10.1034 2.56998L8.06191 4.61149L11.2752 7.91821L13.3634 5.83002C13.4127 5.7807 13.4127 5.70072 13.3634 5.6514L10.282 2.56998ZM10.4266 8.76683L7.21329 5.4601L2.74103 9.93233C2.71835 9.95503 2.70513 9.98552 2.70409 10.0176L2.60047 13.2026C2.5981 13.2755 2.65791 13.3353 2.7308 13.3329L5.91584 13.2293C5.94768 13.2283 5.97827 13.2151 6.00103 13.1924L10.4266 8.76683ZM6.79515 4.18118L9.25488 1.72148C9.77282 1.20353 10.6126 1.20351 11.1306 1.72147L14.212 4.80287C14.7299 5.32082 14.7299 6.16058 14.212 6.67852L6.8496 14.0408C6.61154 14.2789 6.29168 14.4177 5.95487 14.4286L2.76984 14.5323C2.00378 14.5572 1.37619 13.9295 1.40111 13.1636L1.50472 9.97854C1.51568 9.64192 1.6543 9.32204 1.89246 9.08385L5.44364 5.53267L6.79515 4.18118Z"
                                                fill="#19191A"
                                            />
                                        </svg>
                                    }
                                />
                            </Tooltip>
                            <Tooltip
                                hasArrow
                                isDisabled={isLoading}
                                openDelay={500}
                                label={getTitle('37', 'Delete')}
                                // placement={'left'}
                                padding={'8px 16px'}
                                borderRadius={'4px'}
                                backgroundColor={'#19191A'}
                                fontWeight={600}
                                fontSize={'12px'}
                                shouldWrapChildren
                            >
                                <ApplyV2Button
                                    className={clsx(
                                        `${baseClass}__header--buttons-btn ${baseClass}__header--buttons-btn-delete`,
                                        {
                                            [`${className}__header--buttons-btn-delete`]: className,
                                        }
                                    )}
                                    variant={'transparent'}
                                    size={'small'}
                                    onClick={() => {
                                        if (onDelete) {
                                            onDelete()
                                        }
                                    }}
                                    leftIcon={
                                        <svg
                                            width="16"
                                            height="16"
                                            viewBox="0 0 16 16"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <path
                                                fillRule="evenodd"
                                                clipRule="evenodd"
                                                d="M6.5834 1.9334C6.56355 1.9334 6.53877 1.9414 6.51618 1.96503C6.4927 1.98957 6.47507 2.02837 6.47507 2.07414V3.69636H9.52507V2.07414C9.52507 2.02837 9.50743 1.98957 9.48395 1.96503C9.46136 1.9414 9.43658 1.9334 9.41673 1.9334H6.5834ZM10.7251 3.69636V2.07414C10.7251 1.727 10.5934 1.38897 10.3512 1.13568C10.1082 0.881485 9.7726 0.733398 9.41673 0.733398H6.5834C6.22753 0.733398 5.89197 0.881485 5.64889 1.13568C5.40668 1.38897 5.27507 1.727 5.27507 2.07414V3.69636H3.0485C3.04443 3.69632 3.04036 3.69632 3.03628 3.69636H2.3334C2.00203 3.69636 1.7334 3.96499 1.7334 4.29636C1.7334 4.62773 2.00203 4.89636 2.3334 4.89636H2.48764L3.15022 13.2111C3.15664 13.7455 3.36225 14.2615 3.73135 14.6475C4.10727 15.0406 4.623 15.2667 5.16673 15.2667H10.8334C11.3771 15.2667 11.8929 15.0406 12.2688 14.6475C12.6379 14.2615 12.8435 13.7455 12.8499 13.2111L13.5125 4.89636H13.6667C13.9981 4.89636 14.2667 4.62773 14.2667 4.29636C14.2667 3.96499 13.9981 3.69636 13.6667 3.69636H12.9639C12.9598 3.69632 12.9557 3.69632 12.9516 3.69636H10.7251ZM3.69145 4.89636L4.34817 13.1376C4.34943 13.1534 4.35007 13.1693 4.35007 13.1853C4.35007 13.4275 4.44233 13.6547 4.59864 13.8181C4.75408 13.9807 4.95902 14.0667 5.16673 14.0667H10.8334C11.0411 14.0667 11.2461 13.9807 11.4015 13.8181C11.5578 13.6547 11.6501 13.4275 11.6501 13.1853C11.6501 13.1693 11.6507 13.1534 11.652 13.1376L12.3087 4.89636H3.69145ZM6.5834 6.65932C6.91477 6.65932 7.1834 6.92795 7.1834 7.25932V11.7038C7.1834 12.0351 6.91477 12.3038 6.5834 12.3038C6.25203 12.3038 5.9834 12.0351 5.9834 11.7038V7.25932C5.9834 6.92795 6.25203 6.65932 6.5834 6.65932ZM9.41673 6.65932C9.7481 6.65932 10.0167 6.92795 10.0167 7.25932V11.7038C10.0167 12.0351 9.7481 12.3038 9.41673 12.3038C9.08536 12.3038 8.81673 12.0351 8.81673 11.7038V7.25932C8.81673 6.92795 9.08536 6.65932 9.41673 6.65932Z"
                                                fill="currentColor"
                                            />
                                        </svg>
                                    }
                                />
                            </Tooltip>
                        </div>
                    )}
                </div>
                {headerDescription && (
                    <div
                        className={clsx(`${baseClass}__header--description`, {
                            [`${className}__header--description`]: className,
                        })}
                    >
                        {headerDescription}
                    </div>
                )}
            </div>
            <div
                className={clsx(`${baseClass}__wrapper`, {
                    [`${className}__wrapper`]: className,
                })}
            >
                {!!phoneValue && (
                    <ApplyV2DataRow
                        // fieldType={'mobilePhone'}
                        fieldType={'tel'}
                        className={clsx(`${baseClass}__mobilePhone`, {
                            [`${className}__mobilePhone`]: className,
                        })}
                    >
                        {formatPhoneNumber(phone)}
                    </ApplyV2DataRow>
                )}
                {phoneSection && (
                    <ApplyV2DataRow
                        // fieldType={'mobilePhone'}
                        fieldType={'tel'}
                        className={clsx(`${baseClass}__mobilePhone`, {
                            [`${className}__mobilePhone`]: className,
                        })}
                    >
                        {phoneSection}
                    </ApplyV2DataRow>
                )}

                {emailSection && (
                    <ApplyV2DataRow
                        fieldType={'email'}
                        className={clsx(`${baseClass}__email`, {
                            [`${className}__email`]: className,
                        })}
                    >
                        {emailSection}
                    </ApplyV2DataRow>
                )}
                {dateSection && (
                    <ApplyV2DataRow
                        fieldType={'date'}
                        className={clsx(`${baseClass}__date`, {
                            [`${className}__date`]: className,
                        })}
                    >
                        {dateSection}
                    </ApplyV2DataRow>
                )}
                {!!periodDate && (
                    <ApplyV2DataRow
                        fieldType={'date'}
                        className={clsx(`${baseClass}__date`, {
                            [`${className}__date`]: className,
                        })}
                    >
                        <div
                            className={clsx(`${baseClass}__date--row`, {
                                [`${className}__date--row`]: className,
                            })}
                        >
                            <span
                                className={clsx(`${baseClass}__date--row-period`, {
                                    [`${className}__date--row-period`]: className,
                                })}
                            >
                                {periodDate}
                            </span>
                            {!!duration && date.endDate && (
                                <span
                                    className={clsx(`${baseClass}__date--row-duration`, {
                                        [`${className}__date--row-duration`]: className,
                                    })}
                                >
                                    ({duration})
                                </span>
                            )}
                        </div>
                    </ApplyV2DataRow>
                )}
                {localeSection && (
                    <ApplyV2DataRow
                        fieldType={'location'}
                        className={clsx(`${baseClass}__location`, {
                            [`${className}__location`]: className,
                        })}
                    >
                        {localeSection}
                    </ApplyV2DataRow>
                )}
                {children && (
                    <div
                        className={clsx(`${baseClass}__children`, {
                            [`${className}__children`]: className,
                            // [`${baseClass}__children--collapsed`]: !isExpanded && needsExpand,
                        })}
                    >
                        <div
                            className={clsx(`${baseClass}__children-content`, {
                                [`${className}__children-content`]: className,
                                [`${baseClass}__children-content--collapsed`]: !isExpanded && needsExpand,
                            })}
                            ref={textRef}
                        >
                            {children}
                        </div>
                        {needsExpand && (
                            <button
                                type={'button'}
                                className={`${baseClass}__expand-button`}
                                onClick={() => setIsExpanded(!isExpanded)}
                            >
                                <span>{isExpanded ? 'Show less' : 'Show more'}</span>
                                <span
                                    className={clsx(`${baseClass}__expand-button-arrow`, {
                                        [`${baseClass}__expand-button-arrow-isExpanded`]: isExpanded,
                                    })}
                                ></span>
                            </button>
                        )}
                    </div>
                )}

                {files && files.length > 0 && (
                    <FileUploaderV2List
                        files={files || []}
                        className={clsx(`${baseClass}__files`, {
                            [`${className}__files`]: className,
                        })}
                        isShowDelete={false}
                        isShowParseInfo={false}
                    ></FileUploaderV2List>
                )}
            </div>
        </div>
    )
}

export default ApplyV2Card
